import React from 'react';
import { ExternalLink } from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, LabelList,
  BarChart, Bar,
} from 'recharts';
import { JIRA_BASE, severityClass, statusClass } from './fixStatusTableHelpers';
import RejectedTable from './RejectedTable';

const SEVERITIES = ['critical', 'major', 'minor'];

function everyOtherDayLabel(color) {
  return ({ x, y, value, index }) => {
    if (index % 2 !== 0) {
      return null;
    }
    return (
      <text x={x} y={y - 8} textAnchor="middle" fontSize={10} fill={color}>
        {value}
      </text>
    );
  };
}

const STATUS_COLORS = { Done: '#4ade80', Backlog: '#f87171' };

function legendFor(mode) {
  if (mode === 'none') {
    return null;
  }
  const props = mode === 'left'
    ? { layout: 'vertical', verticalAlign: 'middle', align: 'left' }
    : {};
  return <Legend {...props} formatter={(v) => <span style={{ fontSize: 12 }}>{v}</span>} />;
}

const CHART_HEIGHT = 330;

function StatusUnitRow({ fixProgress, unitData }) {
  const pieLegend = legendFor('top');
  const barLegend = legendFor('left');
  return (
    <div className="fix-status-top-row" style={{ gridTemplateColumns: '1.2fr 2fr' }}>
      <div className="fix-status-col">
        <div className="section-header">Bugs by Status</div>
        <div className="section-body fix-progress-body">
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <PieChart>
              <Pie
                data={fixProgress}
                cx="50%"
                cy="50%"
                innerRadius={73}
                outerRadius={118}
                dataKey="value"
                label={({ value }) => `${value}`}
                labelLine={false}
              >
                {
                  fixProgress.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.name] ?? '#60a5fa'} />
                  ))
                }
              </Pie>
              <Tooltip />
              {pieLegend}
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="fix-status-col">
        <div className="section-header">Fixed vs Opened by Unit</div>
        <div className="section-body fix-progress-body">
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={unitData} margin={{ top: 20, right: 10, left: 0, bottom: 65 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="unit" tick={{ fontSize: 10 }} angle={-90} textAnchor="end" interval={0} height={88} />
              <YAxis />
              <Tooltip />
              {barLegend}
              <Bar dataKey="fixed" name="Fixed" stackId="a" fill="#4ade80">
                <LabelList dataKey="fixed" position="inside" style={{ fontSize: 9, fill: '#14532d' }} formatter={(v) => (v > 0 ? v : '')} />
              </Bar>
              <Bar dataKey="remaining" name="Open" stackId="a" fill="#93c5fd">
                <LabelList dataKey="opened" position="top" style={{ fontSize: 9, fill: '#1e3a8a' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function QuickFixTable({ issues }) {
  if (!issues || issues.length === 0) {
    return (
      <div>
        <div className="section-header">Fixed by QT</div>
        <div className="section-body" style={{ color: 'var(--cmp-text-muted)', fontSize: '0.85rem' }}>
          No quick-fix issues found.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">Fixed by QT <span style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: 6 }}></span></div>
      <div className="section-body" style={{ padding: 0 }}>
        <div className="issues-table-wrap">
          <table className="issues-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Title</th>
                <th>Severity</th>
                <th>Unit</th>
                <th>Status</th>
                <th>Assignee</th>
                <th>Resolution</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((issue) => (
                <tr key={issue.key}>
                  <td>
                    <a href={`${JIRA_BASE}/${issue.key}`} target="_blank" rel="noopener noreferrer">
                      {issue.key} <ExternalLink size={11} style={{ verticalAlign: 'middle', opacity: 0.6 }} />
                    </a>
                  </td>
                  <td>{issue.summary}</td>
                  <td>
                    {issue.severity
                      ? <span className={`issue-type-pill ${severityClass(issue.severity)}`}>{issue.severity}</span>
                      : '—'}
                  </td>
                  <td>{issue.unit || '—'}</td>
                  <td>
                    <span className={`status-chip ${statusClass(issue.status)}`}>{issue.status || '—'}</span>
                  </td>
                  <td>{issue.assignee || '—'}</td>
                  <td>{issue.resolution || '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} style={{ padding: '6px 10px', fontSize: '0.8rem', color: 'var(--cmp-text-muted)', borderTop: '2px solid var(--cmp-border)' }}>
                  {issues.length} {issues.length === 1 ? 'issue' : 'issues'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// The "Fixed by QT" card and table track the Quality team's own quick fixes, so only team members see them.
function ConfirmedBugsFixStatus({
  fixProgress, fixDone, openedBySeverity, fixByUnit, trendData, quickFixIssues,
  isQualityTeamMember = false, rejectedIssues, rejectedBy, onRejectedByChange,
}) {
  const unitData = (fixByUnit || []).map((d) => ({ ...d, remaining: Math.max(d.opened - d.fixed, 0) }));

  return (
    <div className="fix-status-sections">

      {/* Top row: Fix Progress + Fix by Unit side by side */}
      <StatusUnitRow fixProgress={fixProgress} unitData={unitData} />

      {/* Fixed vs opened by severity */}
      <div>
        <div className="section-header">Fixed vs Opened by Severity</div>
        <div className="section-body">
          <div className="fix-vs-open-row">
            {SEVERITIES.map((sev) => {
              const fixed = fixDone[sev];
              const opened = openedBySeverity[sev];
              const pct = opened > 0 ? Math.round((fixed / opened) * 100) : 0;
              return (
                <div key={sev} className={`fix-vs-open-card ${sev}`}>
                  <span className="fd-label">{sev.charAt(0).toUpperCase() + sev.slice(1)} Bugs</span>
                  <span className="fvo-ratio">{fixed}/{opened}</span>
                  <div className="fvo-bar-track">
                    <div className="fvo-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="fvo-pct">{pct}%</span>
                </div>
              );
            })}
            {(() => {
              const totalFixed = fixDone.critical + fixDone.major + fixDone.minor;
              const totalOpened = openedBySeverity.critical + openedBySeverity.major + openedBySeverity.minor;
              const totalPct = totalOpened > 0 ? Math.round((totalFixed / totalOpened) * 100) : 0;
              return (
                <div className="fix-vs-open-card total">
                  <span className="fd-label">Total Bugs</span>
                  <span className="fvo-ratio">{totalFixed}/{totalOpened}</span>
                  <div className="fvo-bar-track">
                    <div className="fvo-bar-fill" style={{ width: `${totalPct}%` }} />
                  </div>
                  <span className="fvo-pct">{totalPct}%</span>
                </div>
              );
            })()}
            {isQualityTeamMember && (() => {
              const qtTotal = (quickFixIssues ?? []).length;
              const qtFixed = (quickFixIssues ?? []).filter(i => i.status === 'Done').length;
              const qtPct = qtTotal > 0 ? Math.round((qtFixed / qtTotal) * 100) : 0;
              return (
                <div className="fix-vs-open-card qt">
                  <span className="fd-label">Fixed by QT</span>
                  <span className="fvo-ratio">{qtFixed}/{qtTotal}</span>
                  <div className="fvo-bar-track">
                    <div className="fvo-bar-fill" style={{ width: `${qtPct}%` }} />
                  </div>
                  <span className="fvo-pct">{qtPct}%</span>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Bottom: Open bugs trend */}
      < div >
        <div className="section-header">Open Bugs by Severity over time</div>
        <div className="section-body">
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
              <YAxis />
              <Tooltip />
              <Legend formatter={(v) => <span style={{ fontSize: 12 }}>{v}</span>} />
              <Line dataKey="total" stroke="#6f020d" strokeWidth={2} dot={false} name="Total">
                <LabelList dataKey="total" content={everyOtherDayLabel('#6f020d')} />
              </Line>
              <Line dataKey="critical" stroke="#b91c1c" strokeWidth={1.5} dot={false} name="Critical">
                <LabelList dataKey="critical" content={everyOtherDayLabel('#b91c1c')} />
              </Line>
              <Line dataKey="major" stroke="#e07b7b" strokeWidth={1.5} dot={false} name="Major">
                <LabelList dataKey="major" content={everyOtherDayLabel('#e07b7b')} />
              </Line>
              <Line dataKey="minor" stroke="#fbb8b8" strokeWidth={1.5} dot={false} name="Minor">
                <LabelList dataKey="minor" content={everyOtherDayLabel('#fbb8b8')} />
              </Line>
            </LineChart >
          </ResponsiveContainer >
        </div >
      </div >

      {/* Bottom: Daily bugs added vs fixed */}
      <div>
        <div className="section-header">Bugs Added vs Fixed per Day</div>
        <div className="section-body">
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
              <YAxis />
              <Tooltip />
              <Legend formatter={(v) => <span style={{ fontSize: 12 }}>{v}</span>} />
              <Bar dataKey="added" name="Added" fill="#f87171">
                <LabelList dataKey="added" position="top" style={{ fontSize: 9, fill: '#b91c1c' }} formatter={(v) => (v > 0 ? v : '')} />
              </Bar>
              <Bar dataKey="fixed" name="Fixed" fill="#4ade80">
                <LabelList dataKey="fixed" position="top" style={{ fontSize: 9, fill: '#166534' }} formatter={(v) => (v > 0 ? v : '')} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom: rejected issues, then the Quality team's own quick fixes */}
      <RejectedTable issues={rejectedIssues} rejectedBy={rejectedBy} onRejectedByChange={onRejectedByChange} />
      {isQualityTeamMember && <QuickFixTable issues={quickFixIssues} />}

    </div >
  );
}

export default ConfirmedBugsFixStatus;
